import { useMemo } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { HourStat } from '@/utils/analytics'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface TimeOfDayChartProps {
  stats: HourStat[]
}

function fmtHour(h: number): string {
  if (h === 0) return '12a'
  if (h === 12) return '12p'
  if (h < 12) return `${h}a`
  return `${h - 12}p`
}

export default function TimeOfDayChart({ stats }: TimeOfDayChartProps) {
  const data = useMemo(() => {
    const max = Math.max(...stats.map(s => s.drinks), 1)
    return {
      labels: stats.map(s => fmtHour(s.hour)),
      datasets: [
        {
          label: 'Drinks',
          data: stats.map(s => s.drinks),
          backgroundColor: stats.map(s => {
            const ratio = s.drinks / max
            if (ratio > 0.75) return '#ef4444'
            if (ratio > 0.5) return '#f97316'
            if (ratio > 0.25) return '#f59e0b'
            return '#2dd4bf'
          }),
          borderRadius: 2,
        },
      ],
    }
  }, [stats])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgb(30, 41, 59)',
        titleColor: 'rgb(248, 250, 252)',
        bodyColor: 'rgb(148, 163, 184)',
        borderColor: 'rgb(71, 85, 105)',
        borderWidth: 1,
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const s = stats[items[0]?.dataIndex]
            if (!s) return ''
            const next = (s.hour + 1) % 24
            return `${fmtHour(s.hour)} – ${fmtHour(next)}`
          },
          label: (item: { parsed: { y: number } }) => `${item.parsed.y} drink${item.parsed.y === 1 ? '' : 's'}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: 'rgb(148, 163, 184)',
          autoSkip: false,
          maxRotation: 0,
          callback: function(this: { getLabelForValue: (i: number) => string }, _value: string | number, index: number) {
            // Show every 3rd hour to avoid crowding
            return index % 3 === 0 ? this.getLabelForValue(index) : ''
          },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: 'rgb(148, 163, 184)', precision: 0 },
        grid: { color: 'rgba(71, 85, 105, 0.3)' },
      },
    },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }), [stats]) as any

  const hasData = stats.some(s => s.drinks > 0)
  if (!hasData) {
    return (
      <div className="card">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Drinks by time of day</h2>
        <p className="text-sm text-[var(--text-secondary)]">No data in this period.</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Drinks by time of day</h2>
        <p className="text-xs text-[var(--text-secondary)]">finish time, local</p>
      </div>
      <div className="h-48">
        <Bar data={data} options={options} />
      </div>
    </div>
  )
}
